import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import { FORMATIONS, PHASES, todayISO, unitOf, type PlayPhase } from '@shared/schemas';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { dirFor } from '@/i18n';
import {
  useConfig,
  useCreateFormation,
  useCopyPhase,
  useDeleteFormation,
  useFormationPlan,
  useFormationPlans,
  useSavePhase,
  useTeams,
  useUpdateFormation,
  type PlacementRow,
} from '@/app/queries';
import { useSession } from '@/app/session';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { formatDate, sheetName } from '@/lib/format';
import { FormationBoard } from '@/ui/FormationBoard';
import type { RenderPlacement, SheetHeader } from '@/diagrams/formationCanvas';
import { Button, Spinner } from '@/ui/Button';
import { SquadPitch } from '@/routes/formations/SquadPitch';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { Chip } from '@/ui/Chip';

type Draft = Omit<PlacementRow, 'id' | 'phase'>;

/**
 * The positions view.
 *
 * One sheet holds all five phases of the same team, because the useful thing is
 * seeing that the shape with the ball is not the shape without it — and being
 * able to print either, or all of them, for the dressing-room wall.
 */
export function Formations({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const { user } = useSession();
  const teams = useTeams();
  const config = useConfig();
  const teamList = teams.data ?? [];
  const [teamId, setTeamId] = useState<string | null>(null);
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const plans = useFormationPlans(activeTeamId ?? undefined);
  const [planId, setPlanId] = useState<string | null>(null);
  const activePlanId = planId ?? plans.data?.[0]?.id ?? null;
  const plan = useFormationPlan(activePlanId);

  const create = useCreateFormation();
  const update = useUpdateFormation();
  const savePhase = useSavePhase(activePlanId ?? '');
  const copyPhase = useCopyPhase(activePlanId ?? '');
  const remove = useDeleteFormation();
  const { busy, formError, submit } = useAuthForm();

  const [phase, setPhase] = useState<PlayPhase>('base');
  const [draft, setDraft] = useState<Draft[]>([]);
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [openNew, setOpenNew] = useState(false);
  // The formation sheet answers "who starts"; the squad sheet answers "what
  // have I got". Same pitch, different question, so one toggle rather than two
  // screens that drift apart.
  const [view, setView] = useState<'formation' | 'squad'>('formation');
  const [newPlan, setNewPlan] = useState({ name: '', formationKey: '4-2-3-1', notes: '' });

  const squad = plan.data?.squad ?? [];
  const statuses = config.data?.statuses ?? [];
  const roles = useMemo(() => statuses.filter((s) => s.kind === 'role'), [statuses]);

  const label = (row: { label: string; i18nKey: string | null }) => {
    if (!row.i18nKey) return row.label;
    const translated = t(row.i18nKey);
    return translated === row.i18nKey ? row.label : translated;
  };

  // Load the phase being looked at into an editable draft.
  const key = `${activePlanId}:${phase}`;
  useEffect(() => {
    if (!plan.data || hydratedFor === key) return;
    const rows = plan.data.placements
      .filter((p) => p.phase === phase)
      .sort((a, b) => a.sort - b.sort)
      .map(({ id: _id, phase: _phase, ...rest }) => rest);
    setDraft(rows);
    setSelected(null);
    setHydratedFor(key);
  }, [plan.data, phase, key, hydratedFor]);

  const byPlayer = useMemo(
    () => new Map(squad.map((p) => [p.playerId, p])),
    [squad],
  );

  const rendered: RenderPlacement[] = draft.map((d) => {
    const p = d.playerId ? byPlayer.get(d.playerId) : undefined;
    return {
      x: d.x,
      y: d.y,
      positionKey: d.positionKey,
      label: d.label,
      note: d.note,
      shirtNumber: p?.shirtNumber ?? null,
      name: p ? (p.knownAs ?? `${p.firstName.charAt(0)}. ${p.lastName}`) : null,
      unit: unitOf(d.positionKey ?? ''),
    };
  });

  const onSheet = new Set(draft.map((d) => d.playerId).filter(Boolean) as string[]);
  const bench = squad.filter((p) => !onSheet.has(p.playerId));

  const header = (forPhase: PlayPhase): SheetHeader => ({
    title: plan.data?.plan.name ?? t('formations.title'),
    phaseLabel: t(`formations.phase_${forPhase}`),
    teamName: teamList.find((tm) => tm.id === activeTeamId)?.name ?? '',
    coachName: user?.name ?? '',
    formationKey: plan.data?.plan.formationKey ?? '',
    date: formatDate(todayISO(), locale),
    notes: plan.data?.plan.notes ?? null,
  });

  const renderFor = (forPhase: PlayPhase): RenderPlacement[] =>
    (plan.data?.placements ?? [])
      .filter((p) => p.phase === forPhase)
      .sort((a, b) => a.sort - b.sort)
      .map((d) => {
        const p = d.playerId ? byPlayer.get(d.playerId) : undefined;
        return {
          x: d.x,
          y: d.y,
          positionKey: d.positionKey,
          label: d.label,
          note: d.note,
          shirtNumber: p?.shirtNumber ?? null,
          name: p ? (p.knownAs ?? `${p.firstName.charAt(0)}. ${p.lastName}`) : null,
          unit: unitOf(d.positionKey ?? ''),
        };
      });

  /**
   * An export that fails must say so. A `finally` without a `catch` resets the
   * button and leaves the coach staring at a page that did nothing, with no
   * indication whether to try again.
   */
  const runExport = async (work: () => Promise<void>) => {
    setExporting(true);
    setExportError(null);
    try {
      await work();
    } catch (err) {
      console.error('PDF export failed:', err);
      setExportError(err instanceof Error ? err.message : t('error.generic'));
    } finally {
      setExporting(false);
    }
  };

  const exportOne = () =>
    runExport(async () => {
      const { exportFormationPdf } = await import('@/diagrams/formationPdf');
      await exportFormationPdf(
        rendered,
        header(phase),
        dirFor(locale),
        `${plan.data?.plan.name ?? 'positions'}-${phase}.pdf`,
      );
    });

  const exportAll = () =>
    runExport(async () => {
      const { exportAllPhasesPdf } = await import('@/diagrams/formationPdf');
      await exportAllPhasesPdf(
        PHASES.map((p) => ({ placements: renderFor(p), header: header(p) })),
        dirFor(locale),
        `${plan.data?.plan.name ?? 'positions'}.pdf`,
      );
    });

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
        title={t('formations.title')}
        lead={t('formations.lead')}
        help={localePath(locale, '/guide/match')}
      />
        <Panel>
          <EmptyState title={t('teams.empty')} body={t('formations.noTeam')} />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('formations.title')}
        lead={t('formations.lead')}
        help={localePath(locale, '/guide/match')}
        action={
          // Not while there is nothing to list: the empty state makes the same
          // offer, where a coach is already looking.
          (plans.data ?? []).length === 0 ? null : (
            <Button variant="primary" onClick={() => setOpenNew((v) => !v)}>
              {t('formations.create')}
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        {teamList.length > 1 ? (
          <div className="flex flex-wrap gap-1">
            {teamList.map((tm) => (
              <button
                key={tm.id}
                type="button"
                onClick={() => {
                  setTeamId(tm.id);
                  setPlanId(null);
                  setHydratedFor(null);
                }}
                aria-pressed={tm.id === activeTeamId}
                className={`rounded-(--radius-control) border px-3 py-1.5 text-meta transition-colors ${
                  tm.id === activeTeamId
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-surface text-ink-muted hover:border-ink'
                }`}
              >
                {tm.name}
              </button>
            ))}
          </div>
        ) : null}
        {(plans.data ?? []).length > 0 ? (
          <div className="w-64">
            <Field label={t('formations.name')}>
              {(p) => (
                <SelectInput
                  {...p}
                  value={activePlanId ?? ''}
                  onChange={(e) => {
                    setPlanId(e.target.value);
                    setHydratedFor(null);
                  }}
                >
                  {(plans.data ?? []).map((pl) => (
                    <option key={pl.id} value={pl.id}>
                      {pl.name} — {pl.formationKey}
                    </option>
                  ))}
                </SelectInput>
              )}
            </Field>
          </div>
        ) : null}
      </div>

      {openNew ? (
        <div className="mb-5">
          <Panel title={t('formations.create')}>
            <Section title={t('formations.create')}>
              <div className="grid gap-x-5 gap-y-2 sm:grid-cols-3">
                <Field label={t('formations.name')}>
                  {(p) => (
                    <TextInput
                      {...p}
                      value={newPlan.name}
                      onChange={(e) => setNewPlan((n) => ({ ...n, name: e.target.value }))}
                      placeholder="4-2-3-1 base"
                    />
                  )}
                </Field>
                <Field label={t('formations.formation')}>
                  {(p) => (
                    <SelectInput
                      {...p}
                      value={newPlan.formationKey}
                      onChange={(e) =>
                        setNewPlan((n) => ({ ...n, formationKey: e.target.value }))
                      }
                    >
                      {FORMATIONS.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.key}
                        </option>
                      ))}
                    </SelectInput>
                  )}
                </Field>
                <div className="sm:col-span-3">
                  <Field label={t('formations.notes')} optional>
                    {(p) => (
                      <TextArea
                        {...p}
                        value={newPlan.notes}
                        onChange={(e) => setNewPlan((n) => ({ ...n, notes: e.target.value }))}
                      />
                    )}
                  </Field>
                </div>
              </div>
            </Section>
            <div className="flex gap-2 border-t border-line px-4 py-4">
              <Button
                variant="primary"
                busy={create.isPending}
                disabled={!newPlan.name.trim() || !activeTeamId}
                onClick={async () => {
                  const created = await create.mutateAsync({
                    teamId: activeTeamId!,
                    name: newPlan.name,
                    formationKey: newPlan.formationKey,
                    notes: newPlan.notes || undefined,
                  });
                  setPlanId(created.id);
                  setHydratedFor(null);
                  setOpenNew(false);
                  setNewPlan({ name: '', formationKey: '4-2-3-1', notes: '' });
                }}
              >
                {t('formations.save')}
              </Button>
              <Button variant="quiet" onClick={() => setOpenNew(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </Panel>
        </div>
      ) : null}

      {plans.isLoading ? (
        <p className="flex items-center gap-2 text-ink-muted">
          <Spinner /> {t('common.loading')}
        </p>
      ) : (plans.data ?? []).length === 0 ? (
        <Panel>
          <EmptyState
            title={t('formations.empty')}
            body={t('formations.emptyBody')}
            action={
              <Button variant="primary" onClick={() => setOpenNew(true)}>
                {t('formations.create')}
              </Button>
            }
          />
        </Panel>
      ) : plan.isLoading ? (
        <p className="flex items-center gap-2 text-ink-muted">
          <Spinner /> {t('common.loading')}
        </p>
      ) : (
        <>
          {formError || exportError ? (
            <div className="mb-4">
              <Notice tone="risk">{formError ?? exportError}</Notice>
            </div>
          ) : null}

          <div className="mb-4 flex flex-wrap gap-1.5">
            {(['formation', 'squad'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={v === view}
                onClick={() => setView(v)}
                className={`min-h-11 rounded-(--radius-control) border px-3 py-2 font-condensed text-meta font-semibold transition-colors ${
                  v === view
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
                }`}
              >
                {t(v === 'formation' ? 'formations.viewFormation' : 'formations.viewSquad')}
              </button>
            ))}
          </div>

          {view === 'squad' && activeTeamId ? (
            <SquadPitch locale={locale} teamId={activeTeamId} />
          ) : null}

          <div className={`mb-4 flex-wrap gap-1 ${view === 'squad' ? 'hidden' : 'flex'}`}>
            {PHASES.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={p === phase}
                onClick={() => {
                  setPhase(p);
                  setHydratedFor(null);
                }}
                className={`min-h-11 rounded-(--radius-control) border px-3 py-2 text-meta transition-colors ${
                  p === phase
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
                }`}
              >
                {t(`formations.phase_${p}`)}
              </button>
            ))}
          </div>

          <div
            className={`gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] ${
              view === 'squad' ? 'hidden' : 'grid'
            }`}
          >
            <Panel
              title={t(`formations.phase_${phase}`)}
              action={
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    className="!text-meta"
                    busy={exporting}
                    onClick={() => void exportOne()}
                  >
                    {t('formations.exportPhase')}
                  </Button>
                  <Button
                    variant="secondary"
                    className="!text-meta"
                    busy={exporting}
                    onClick={() => void exportAll()}
                  >
                    {t('formations.exportAll')}
                  </Button>
                </div>
              }
            >
              <p className="border-b border-line px-4 py-3 text-meta text-ink-muted">
                {t('formations.phaseHint')}
              </p>
              <div className="p-4">
                <FormationBoard
                  placements={rendered}
                  selectedIndex={selected}
                  onSelect={setSelected}
                  onMove={(index, x, y) =>
                    setDraft((d) => d.map((p, i) => (i === index ? { ...p, x, y } : p)))
                  }
                />
              </div>

              {selected !== null && draft[selected] ? (
                <div className="border-t border-line px-4 py-4">
                  <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
                    <Field label={t('formations.pickPlayer')}>
                      {(p) => (
                        <SelectInput
                          {...p}
                          value={draft[selected]?.playerId ?? ''}
                          onChange={(e) =>
                            setDraft((d) =>
                              d.map((x, i) =>
                                i === selected
                                  ? { ...x, playerId: e.target.value || null }
                                  : // One player cannot stand in two places.
                                    x.playerId === e.target.value
                                    ? { ...x, playerId: null }
                                    : x,
                              ),
                            )
                          }
                        >
                          <option value="">{t('formations.none')}</option>
                          {squad.map((p2) => (
                            <option key={p2.playerId} value={p2.playerId}>
                              {p2.shirtNumber ? `${p2.shirtNumber} · ` : ''}
                              {sheetName(p2.firstName, p2.lastName)}
                            </option>
                          ))}
                        </SelectInput>
                      )}
                    </Field>
                    <Field label={t('formations.slotNote')} optional>
                      {(p) => (
                        <TextInput
                          {...p}
                          value={draft[selected]?.note ?? ''}
                          onChange={(e) =>
                            setDraft((d) =>
                              d.map((x, i) =>
                                i === selected ? { ...x, note: e.target.value || null } : x,
                              ),
                            )
                          }
                        />
                      )}
                    </Field>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-4">
                <Button
                  variant="primary"
                  busy={busy}
                  onClick={() =>
                    void submit(async () => {
                      await savePhase.mutateAsync({
                        phase,
                        placements: draft.map((d, i) => ({ ...d, sort: i * 10 })),
                      });
                    })
                  }
                >
                  {t('formations.save')}
                </Button>
                <SelectInput
                  value=""
                  aria-label={t('formations.copyFrom')}
                  className="w-56 text-meta"
                  onChange={(e) => {
                    if (!e.target.value) return;
                    void copyPhase.mutateAsync({
                      from: phase,
                      to: [e.target.value as PlayPhase],
                    });
                  }}
                >
                  <option value="">{t('formations.copyFrom')}</option>
                  {PHASES.filter((p) => p !== phase).map((p) => (
                    <option key={p} value={p}>
                      {t(`formations.phase_${p}`)}
                    </option>
                  ))}
                </SelectInput>
                <Button
                  variant="danger"
                  className="ms-auto !text-meta"
                  onClick={async () => {
                    if (!activePlanId || !confirm(t('formations.deleteConfirm'))) return;
                    await remove.mutateAsync(activePlanId);
                    setPlanId(null);
                    setHydratedFor(null);
                  }}
                >
                  {t('formations.delete')}
                </Button>
              </div>
            </Panel>

            <div className="grid gap-5">
              <Panel title={t('formations.unassigned')} count={bench.length}>
                {bench.length === 0 ? (
                  <p className="px-4 py-4 text-meta text-ink-faint">{t('formations.none')}</p>
                ) : (
                  <ul className="divide-y divide-line">
                    {bench.map((p) => {
                      const role = roles.find((r) => r.key === p.roleKey);
                      return (
                        <li
                          key={p.playerId}
                          className="flex items-center gap-2 px-4 py-2 text-meta"
                        >
                          <span className="tabular w-6 shrink-0 font-condensed text-ink-muted">
                            {p.shirtNumber ?? '–'}
                          </span>
                          <span className="min-w-0 flex-1 truncate">
                            {sheetName(p.firstName, p.lastName)}
                          </span>
                          {role ? <Chip muted>{label(role)}</Chip> : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Panel>

              <Panel title={t('formations.formation')}>
                <div className="grid gap-x-5 gap-y-2 p-4">
                  <Field label={t('formations.formation')}>
                    {(p) => (
                      <SelectInput
                        {...p}
                        value={plan.data?.plan.formationKey ?? ''}
                        onChange={(e) => {
                          if (!activePlanId || !plan.data) return;
                          void update
                            .mutateAsync({
                              planId: activePlanId,
                              input: {
                                name: plan.data.plan.name,
                                formationKey: e.target.value,
                                notes: plan.data.plan.notes ?? undefined,
                                reshape: true,
                              },
                            })
                            .then(() => setHydratedFor(null));
                        }}
                      >
                        {FORMATIONS.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.key}
                          </option>
                        ))}
                      </SelectInput>
                    )}
                  </Field>
                  <p className="text-meta text-ink-faint">{t('formations.reshapeHint')}</p>
                </div>
              </Panel>
            </div>
          </div>
        </>
      )}
    </>
  );
}
